/**
 * Floretti 3.0 — formaatstaffel met geurkeuze.
 *
 * Kies een aantal, verdeel dat over de geuren, in de mand. Elke geur wordt een
 * eigen regel met een echte variant, dus het magazijn pickt losse pakken die
 * het al kent en de voorraad per geur klopt.
 *
 * Over de korting: themacode kan geen prijs verlagen. De staffelkorting komt
 * uit een automatische korting op aantal in Shopify. Staat "Kortingen staan
 * live" uit, dan rekent dit blok bewust zónder korting — liever een eerlijke
 * optelsom dan een bedrag dat de winkelmand niet waarmaakt.
 */
class FlorettiSubscriptionPlanner extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-planner-data]');
    if (!raw) return;

    try {
      const data = JSON.parse(raw.textContent);
      /* Teksten komen vertaald uit Liquid; JS kan zelf niet bij de
         locale-bestanden. Plaatshouders __A__, __N__, __P__ en __M__ vullen we
         hier in, zodat de zinsbouw per taal kan verschillen. */
      this.t = data.labels || {};
      this.tiers = (data.tiers || []).filter((t) => t.count > 0);
      /* Niet filteren op voorraad. Een uitverkochte geur hoort zichtbaar te
         blijven met een duidelijke melding; hem weglaten liet eerder de hele
         sectie verdwijnen zodra alles op was. */
      this.scents = data.scents || [];
    } catch (error) {
      return;
    }
    if (!this.tiers.length || !this.scents.length) return;

    this.tiers.sort((a, b) => a.count - b.count);
    this.discountsLive = this.dataset.discountsLive === 'true';
    this.subPercent = parseFloat(this.dataset.subPercent) || 0;
    this.subMin = (parseFloat(this.dataset.subMin) || 0) * 100;
    this.intervals = (this.dataset.intervals || '3,6,9,12')
      .split(',')
      .map((n) => parseInt(n.trim(), 10))
      .filter((n) => n > 0)
      .sort((a, b) => a - b);
    this.interval = null;
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.rootUrl = this.dataset.rootUrl || '/';

    const best = this.tiers.findIndex((t) => t.best);
    this.index = best >= 0 ? best : this.tiers.length - 1;

    /* Verdeling over geuren. Start op de eerste leverbare geur, zodat er
       altijd een geldige keuze klaarstaat. Is niets leverbaar, dan blijft
       alles op nul en meldt de sectie dat netjes. */
    this.counts = this.scents.map(() => 0);
    const eerstLeverbaar = this.scents.findIndex((s) => s.available);
    if (eerstLeverbaar >= 0) this.counts[eerstLeverbaar] = this.tiers[this.index].count;

    this.onceInput = this.querySelector('[data-planner-once]');
    this.subToggle = this.querySelector('[data-planner-sub]');
    this.planFlag = this.querySelector('[data-planner-plan-flag]');
    this.benefitDiscount = this.querySelector('[data-planner-benefit-discount]');
    this.benefitTemplate = this.benefitDiscount ? this.benefitDiscount.textContent : '';
    this.submit = this.querySelector('[data-planner-submit]');
    this.ctaOnce = this.submit ? this.submit.textContent.trim() : '';

    this.el = {
      image: this.querySelector('[data-planner-image]'),
      title: this.querySelector('[data-planner-title]'),
      meta: this.querySelector('[data-planner-meta]'),
      todo: this.querySelector('[data-planner-todo]'),
      planRow: this.querySelector('[data-planner-plan-row]'),
      planTitle: this.querySelector('[data-planner-plan-title]'),
      planNote: this.querySelector('[data-planner-plan-note]'),
      deliveryOnce: this.querySelector('[data-planner-delivery-once]'),
      deliverySub: this.querySelector('[data-planner-delivery-sub]'),
      oncePrice: this.querySelector('[data-planner-once-price]'),
      subPrice: this.querySelector('[data-planner-sub-price]'),
      totalPer: this.querySelector('[data-planner-total-per]'),
      total: this.querySelector('[data-planner-total]'),
      was: this.querySelector('[data-planner-was]'),
      badge: this.querySelector('[data-planner-badge]'),
      intervalHint: this.querySelector('[data-planner-interval-hint]'),
      note: this.querySelector('[data-planner-note]'),
    };

    this.querySelectorAll('[data-planner-tier]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.index = parseInt(btn.dataset.plannerTier, 10) || 0;
        this.interval = this.intervalVoor(this.tier);
        this.rebalance();
        this.render();
      });
    });
    this.querySelectorAll('[data-planner-interval]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.interval = parseInt(btn.dataset.plannerInterval, 10);
        this.render();
      });
    });
    this.querySelectorAll('[data-scent-plus]').forEach((btn) => {
      btn.addEventListener('click', () => this.bump(parseInt(btn.dataset.scentPlus, 10), 1));
    });
    this.querySelectorAll('[data-scent-minus]').forEach((btn) => {
      btn.addEventListener('click', () => this.bump(parseInt(btn.dataset.scentMinus, 10), -1));
    });
    this.querySelectorAll('[data-scent-pick]').forEach((btn) => {
      btn.addEventListener('click', () => this.switchScent(parseInt(btn.dataset.scentPick, 10)));
    });
    if (this.onceInput) this.onceInput.addEventListener('change', () => this.render());
    if (this.subToggle) this.subToggle.addEventListener('change', () => this.render());
    if (this.submit) this.submit.addEventListener('click', () => this.addToCart());

    this.interval = this.intervalVoor(this.tier);
    this.render();
  }

  /** Vult een vertaalde tekst met waarden. Ontbreekt de sleutel, dan vallen we
      terug op de meegegeven fallback in plaats van "undefined" te tonen. */
  tekst(sleutel, waarden, fallback) {
    let s = this.decodeHtml(this.t[sleutel]);
    if (!s) return fallback !== undefined ? fallback : '';
    Object.entries(waarden || {}).forEach(([k, v]) => {
      s = s.split(k).join(v);
    });
    return s;
  }

  decodeHtml(s) {
    if (!s || typeof s !== 'string') return s;
    if (!s.includes('&')) return s;
    if (!this.decoder) this.decoder = document.createElement('textarea');
    this.decoder.innerHTML = s;
    return this.decoder.value;
  }

  money(cents) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  route(path) {
    const root = String(this.rootUrl || '/').replace(/\/+$/, '');
    const suffix = String(path || '').replace(/^\/+/, '');
    return `${root}/${suffix}`;
  }

  get tier() {
    return this.tiers[this.index];
  }

  get chosen() {
    return this.counts.reduce((a, b) => a + b, 0);
  }

  /** Eén prijs per pak; alle geuren kosten hetzelfde. */
  get unit() {
    return this.scents[0].price;
  }

  get washesPerPack() {
    return this.scents[0].washes || 132;
  }

  /** Voor prijs per was tonen we de doseringsrichtlijn: halve strip = 132
      wasbeurten. Voor een terugkerende levering rekenen we conservatiever:
      veel klanten gebruiken in de praktijk één hele strip per was. */
  get fullStripWashesPerPack() {
    return Math.max(1, Math.round(this.washesPerPack / 2));
  }

  /** Wisselt de bezoeker van aantal, dan schuift de verdeling mee in plaats
      van de keuze te wissen: te veel eraf achteraan, te weinig erbij vooraan. */
  rebalance() {
    const doel = this.tier.count;
    let nu = this.chosen;
    for (let i = this.counts.length - 1; i >= 0 && nu > doel; i -= 1) {
      const eraf = Math.min(this.counts[i], nu - doel);
      this.counts[i] -= eraf;
      nu -= eraf;
    }
    if (nu < doel) {
      const i = this.scents.findIndex((s) => s.available);
      if (i >= 0) this.counts[i] += doel - nu;
    }
  }

  /**
   * Klik op de geurnaam.
   *
   * Bij één geur in de selectie is dit "ik wil deze": alles gaat naar die
   * geur. Heeft iemand bewust gemengd — 2 Fris Linnen en 2 Lavendel — dan
   * wist dat zijn werk zonder waarschuwing. In dat geval telt de klik als
   * plus, met dezelfde regel als de plusknop: er gaat er één af bij de geur
   * met de meeste stuks.
   */
  switchScent(i) {
    if (Number.isNaN(i) || !this.scents[i] || !this.scents[i].available) return;

    const gemengd = this.counts.filter((n) => n > 0).length > 1;
    if (gemengd) {
      this.bump(i, 1);
      return;
    }

    this.counts = this.counts.map((_, j) => (j === i ? this.tier.count : 0));
    this.render();
  }

  /**
   * Plus en min op een geur.
   *
   * Zit het gekozen aantal al vol, dan blokkeerde de plusknop eerder gewoon.
   * Dat voelt als "ik kan niet van geur wisselen": je moest eerst handmatig
   * aftrekken bij de andere geur. Nu pakt hij er zelf één af bij de geur met
   * de meeste stuks, zodat wisselen één klik is.
   */
  bump(i, delta) {
    if (Number.isNaN(i) || !this.scents[i]) return;
    if (delta > 0 && !this.scents[i].available) return;

    const next = this.counts[i] + delta;
    if (next < 0) return;

    if (delta > 0 && this.chosen >= this.tier.count) {
      const geef = this.counts
        .map((n, j) => ({ n, j }))
        .filter((x) => x.j !== i && x.n > 0)
        .sort((a, b) => b.n - a.n)[0];
      if (!geef) return;
      this.counts[geef.j] -= 1;
    }

    this.counts[i] = next;
    this.render();
  }

  tierPrice(tier) {
    const lijst = this.unit * tier.count;
    if (!this.discountsLive || !tier.discount) return { lijst, nu: lijst };
    /* Shopify rondt de korting per pak naar beneden af (EUR 17,95 - 5% wordt
       EUR 17,06, niet 17,05). Zo rekenen wij ook, anders scheelt het een cent. */
    const perPak = this.unit - Math.floor((this.unit * tier.discount) / 100);
    return { lijst, nu: perPak * tier.count };
  }

  planVoor(scent) {
    const plannen = scent.plans || [];
    if (!plannen.length) return null;
    const interval = String(this.interval || '');
    const exact = plannen.find((p) => new RegExp(`(^|\\D)${interval}(\\D|$)`, 'i').test(p.name || ''));
    return exact || plannen[0];
  }

  abonnementsPrijs() {
    let totaal = 0;
    for (let i = 0; i < this.scents.length; i += 1) {
      if (!this.counts[i]) continue;
      const plan = this.planVoor(this.scents[i]);
      if (!plan || !plan.id || !plan.price) return null;
      totaal += plan.price * this.counts[i];
    }
    return totaal;
  }

  geplandeAbonnementsPrijs(vanaf) {
    return Math.round(vanaf * (100 - this.subPercent)) / 100;
  }

  abonnementGekozen() {
    return Boolean(this.subToggle && this.subToggle.checked && !this.subToggle.disabled);
  }

  /** Prijs per was in centen met één decimaal ("11,6 cent"). Met hele centen
      vallen 3 en 4 pakken samen op 12 cent en zie je het verschil niet. */
  cent(perWasInCenten) {
    const bedrag = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      .format(perWasInCenten);
    return this.tekst('cents', { __A__: bedrag }, `${bedrag} cent`);
  }

  /** Abonnementsprijs voor een trede, of null als die trede geen abonnement
      kan. Alle geuren kosten hetzelfde, dus één planprijs per pak volstaat. */
  tierAbonnementsPrijs(tier) {
    if (this.subPercent <= 0) return null;
    if (this.tierPrice(tier).nu < this.subMin) return null;
    const geur = this.scents.find((sc) => sc.available) || this.scents[0];
    const plan = this.planVoor(geur);
    if (!plan || !plan.price) return null;
    return plan.price * tier.count;
  }

  /** Elke knop toont zijn eigen prijs per was en volgt live de leverkeuze:
      kies je abonnement, dan springen alle treden naar de abonnementsprijs. */
  renderTierPrices() {
    const abonnement = this.abonnementGekozen();
    this.tiers.forEach((tier, i) => {
      const doel = this.querySelector(`[data-tier-per="${i}"]`);
      if (!doel) return;
      const { lijst, nu: eenmalig } = this.tierPrice(tier);
      const abo = abonnement ? this.tierAbonnementsPrijs(tier) : null;
      const nu = abo !== null && abo < eenmalig ? abo : eenmalig;
      const perWasTekst = this.tekst('perWash', { __A__: this.cent(nu / (tier.count * this.washesPerPack)) });
      const was = lijst > nu ? `<s>${this.money(lijst)}</s> ` : '';
      doel.innerHTML = `<b>${perWasTekst}</b><span>${was}${this.money(nu)}</span>`;

      const flag = this.querySelector(`[data-tier-flag="${i}"]`);
      if (flag) {
        const procent = lijst > nu ? Math.round((1 - nu / lijst) * 100) : 0;
        flag.hidden = !procent;
        flag.classList.toggle('is-best', Boolean(tier.best));
        flag.textContent = procent
          ? this.tekst(tier.best ? 'tierBadgeBest' : 'tierBadge', { __P__: procent })
          : '';
      }
    });
  }

  /**
   * Slim leverinterval: hoe groter het pakket, hoe langer het meegaat. Voor
   * abonnementen rekenen we met één hele strip per was en circa 4,5 wassen per
   * week. Dan is één pak ongeveer drie maanden, twee pakken ongeveer zes
   * maanden, enzovoort.
   */
  intervalVoor(tier) {
    const perJaar = 234;
    const maanden = (tier.count * this.fullStripWashesPerPack) / perJaar * 12;
    const keuzes = this.intervals;
    return keuzes.reduce((beste, m) =>
      Math.abs(m - maanden) < Math.abs(beste - maanden) ? m : beste
    , keuzes[0]);
  }

  render() {
    const tier = this.tier;
    const { lijst, nu } = this.tierPrice(tier);

    this.querySelectorAll('[data-planner-tier]').forEach((btn, i) =>
      btn.setAttribute('aria-checked', String(i === this.index))
    );
    this.counts.forEach((n, i) => {
      const out = this.querySelector(`[data-scent-count="${i}"]`);
      if (out) out.textContent = n;
      const el = this.querySelector(`[data-planner-scent="${i}"]`);
      if (!el) return;
      el.classList.toggle('is-chosen', n > 0);
      el.classList.toggle('is-soldout', !this.scents[i].available);
      const pick = el.querySelector('[data-scent-pick]');
      if (pick) {
        pick.setAttribute('aria-pressed', this.counts[i] > 0 ? 'true' : 'false');
        pick.disabled = !this.scents[i].available;
      }
      const plus = el.querySelector('[data-scent-plus]');
      if (plus) plus.disabled = !this.scents[i].available;
      const min = el.querySelector('[data-scent-minus]');
      if (min) min.disabled = n === 0;
    });

    const rest = tier.count - this.chosen;
    if (this.el.todo) {
      this.el.todo.textContent = rest > 0
        ? this.tekst('remaining', { __N__: rest })
        : this.tekst('complete');
      this.el.todo.classList.toggle('is-done', rest === 0);
    }

    /* Beeld en titel volgen de eerst gekozen geur. */
    const eerste = this.counts.findIndex((n) => n > 0);
    const geur = this.scents[eerste >= 0 ? eerste : 0];
    if (this.el.image && geur.image) this.el.image.src = geur.image;
    if (this.el.title) this.el.title.textContent = tier.label;
    if (this.el.meta) {
      const namen = this.scents
        .map((s, i) => (this.counts[i] > 0 ? `${this.counts[i]}× ${s.name}` : null))
        .filter(Boolean)
        .join(' · ');
      const wasbeurten = this.tekst('washes', { __N__: tier.count * this.washesPerPack });
      this.el.meta.textContent = `${wasbeurten}${namen ? ' · ' + namen : ''}`;
    }

    const abonneerbaar = nu >= this.subMin && this.subPercent > 0;
    const echteAbonnementsPrijs = abonneerbaar ? this.abonnementsPrijs() : null;
    const abonnementCheckoutKlaar = echteAbonnementsPrijs !== null;
    const geplandePrijs = abonneerbaar ? this.geplandeAbonnementsPrijs(nu) : nu;

    if (this.subToggle) {
      this.subToggle.disabled = !abonneerbaar || !abonnementCheckoutKlaar;
      if (this.subToggle.disabled && this.subToggle.checked && this.onceInput) this.onceInput.checked = true;
    }

    const geabonneerd = abonneerbaar && this.abonnementGekozen();
    const eind = geabonneerd ? echteAbonnementsPrijs : nu;
    const vergelijkPrijs = abonnementCheckoutKlaar ? echteAbonnementsPrijs : geplandePrijs;
    /* Het getoonde percentage volgt de echte plan-prijzen, niet de themeinstelling:
       Shopify rekent af, dus wat hier staat moet daar altijd mee overeenkomen. */
    /* Met staffelkorting op eenmalig kan het abonnement gelijk of duurder uitkomen
       dan de gekozen trede. Dan beloven we geen extra besparing, alleen de vaste
       levering. De voordeelregel noemt het percentage op de losse prijs. */
    const voordeelTovLos = abonnementCheckoutKlaar && lijst > 0 ? Math.round(((lijst - vergelijkPrijs) / lijst) * 100) : 0;
    const abonnementGoedkoper = abonnementCheckoutKlaar && vergelijkPrijs < nu;

    if (this.el.planRow) this.el.planRow.hidden = !abonnementCheckoutKlaar;
    /* Zolang er geen selling plans zijn kun je hier niets kopen. Dan tonen we
       de hele leveringskeuze niet: geen "binnenkort", geen lagere prijs en
       geen abonnementspagina-belofte vlak voor launch. */
    if (this.el.planTitle) {
      this.el.planTitle.textContent = abonnementGoedkoper
        ? this.tekst('saveWithPlan', {
            /* Percentage op de losse prijs, net als de badges op de treden; het
               bedrag is wat abonneren extra oplevert boven de gekozen trede. */
            __P__: voordeelTovLos,
            __A__: this.money(nu - vergelijkPrijs),
          })
        : this.tekst('planTeaseTitle');
    }
    if (this.planFlag) this.planFlag.hidden = !abonnementCheckoutKlaar;
    if (this.benefitDiscount) {
      this.benefitDiscount.hidden = !abonnementCheckoutKlaar;
      if (abonnementCheckoutKlaar) this.benefitDiscount.textContent = this.benefitTemplate.replace('__P__', voordeelTovLos);
    }
    if (this.el.planNote) {
      this.el.planNote.textContent = abonnementCheckoutKlaar
        ? this.tekst('subscriptionReady')
        : this.tekst('subscriptionUpcoming');
    }
    if (this.el.deliveryOnce) this.el.deliveryOnce.classList.toggle('is-selected', !geabonneerd);
    if (this.el.deliverySub) {
      this.el.deliverySub.classList.toggle('is-selected', geabonneerd);
      this.el.deliverySub.classList.toggle('is-disabled', !abonnementCheckoutKlaar);
    }
    this.renderTierPrices();
    if (this.el.oncePrice) this.el.oncePrice.textContent = this.money(nu);
    if (this.el.subPrice) this.el.subPrice.textContent = abonnementCheckoutKlaar ? this.money(vergelijkPrijs) : '';

    this.querySelectorAll('[data-planner-interval]').forEach((btn) => {
      btn.disabled = false;
      btn.setAttribute(
        'aria-pressed',
        String(parseInt(btn.dataset.plannerInterval, 10) === this.interval)
      );
    });
    if (this.el.intervalHint) {
      const maanden = Math.round((tier.count * this.fullStripWashesPerPack) / 234 * 12);
      this.el.intervalHint.textContent = this.tekst('lasts', { __M__: maanden });
    }

    if (this.el.totalPer) {
      this.el.totalPer.textContent = this.tekst('onlyPerWash', {
        __A__: this.cent(eind / (tier.count * this.washesPerPack)),
      });
    }
    if (this.el.total) this.el.total.textContent = this.money(eind);

    const losGekocht = lijst > eind ? lijst : 0;
    if (this.el.was) {
      this.el.was.textContent = losGekocht
        ? this.tekst('looseBought', { __A__: this.money(losGekocht) })
        : '';
      this.el.was.hidden = !losGekocht;
    }
    if (this.el.badge) {
      this.el.badge.textContent = '';
      this.el.badge.hidden = true;
    }

    const leverbaar = this.scents.some((s) => s.available);
    if (this.submit) this.submit.disabled = rest !== 0 || !leverbaar;
    if (this.submit && this.ctaOnce) {
      const ctaAbonnement = this.tekst('ctaSubscribe');
      this.submit.textContent = geabonneerd && ctaAbonnement ? ctaAbonnement : this.ctaOnce;
    }
    if (this.el.note) {
      if (!leverbaar) {
        this.el.note.textContent = this.tekst('soldOut');
      } else if (rest > 0) {
        this.el.note.textContent = rest === 1
          ? this.tekst('chooseOneMore')
          : this.tekst('chooseMore', { __N__: rest });
      } else if (geabonneerd) {
        this.el.note.textContent = this.tekst('planStarted', { __M__: this.interval }, this.tekst('planPending'));
      } else if (!this.discountsLive && tier.discount) {
        this.el.note.textContent = this.tekst('oneTime');
      } else {
        this.el.note.textContent = this.tekst('oneTime');
      }
    }
  }

  /**
   * Toevoegen en, als er een kortingscode bij deze trede hoort, die meteen
   * laten toepassen.
   *
   * Shopify kent daar een eigen route voor: /discount/CODE?redirect=... zet de
   * code op de sessie en stuurt door. Dat is deterministisch — precies de code
   * die bij dit aantal hoort — in plaats van hopen dat de juiste automatische
   * regel wint. De code hoort in de admin een minimum-aantalvoorwaarde te
   * hebben, zodat hij niet los te misbruiken is.
   *
   * Zonder code blijft het gedrag zoals het was: cart drawer open, geen
   * paginawissel.
   */
  async addToCart() {
    const items = this.scents
      .map((s, i) => {
        if (this.counts[i] <= 0) return null;
        const item = { id: s.id, quantity: this.counts[i] };
        if (this.abonnementGekozen()) {
          const plan = this.planVoor(s);
          if (plan && plan.id) item.selling_plan = plan.id;
        }
        return item;
      })
      .filter(Boolean);
    if (!items.length) return;

    const code = this.discountsLive && !this.abonnementGekozen() ? (this.tier.code || '').trim() : '';

    this.submit.disabled = true;
    try {
      const res = await fetch(this.route('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('cart/add gaf een fout');

      if (code) {
        const terug = encodeURIComponent(this.cartUrl);
        window.location.href = `${this.route(`discount/${encodeURIComponent(code)}`)}?redirect=${terug}`;
        return;
      }

      /* Dawn's cart drawer luistert op dit event; zonder publish blijft de
         mand op de oude stand staan tot een refresh. */
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'floretti-planner' });
      }
      document.querySelector('cart-drawer')?.classList.remove('is-empty');
      document.querySelector('#cart-icon-bubble')?.click();
    } catch (error) {
      if (this.el.note) this.el.note.textContent = this.tekst('addFailed');
    } finally {
      this.submit.disabled = false;
    }
  }
}

if (!customElements.get('floretti-subscription-planner')) {
  customElements.define('floretti-subscription-planner', FlorettiSubscriptionPlanner);
}
